import { useEffect, useMemo, useState } from "react";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

const emptyCategory = {
  id: null,
  name: "",
  description: "",
  parent: "",
  risk_level: "LOW",
  requires_manual_product_review: false,
  display_order: 0,
  is_active: true,
};

function Modal({ title, children, onClose }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card category-modal-card">
        <header className="tazy-modal-header">
          <div><span>CATEGORY SETUP</span><h3>{title}</h3></div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

export default function CategoryManagement() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyCategory);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const canManage = user?.is_platform_staff || user?.is_superuser || ["SUPER_ADMIN", "ADMIN", "OPERATIONS"].includes(user?.account_type);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await phase1Api.categories();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Unable to load marketplace categories.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const parentCategories = useMemo(() => rows.filter((cat) => !cat.parent), [rows]);

  function openCategory(row = null) {
    setForm(row ? {
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      parent: row.parent || "",
      risk_level: row.risk_level || "LOW",
      requires_manual_product_review: Boolean(row.requires_manual_product_review),
      display_order: row.display_order || 0,
      is_active: row.is_active !== false,
    } : emptyCategory);
    setModalOpen(true);
  }

  function update(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function saveCategory(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = {
        ...form,
        parent: form.parent || null,
        display_order: Number(form.display_order) || 0,
      };
      delete payload.id;
      if (form.id) await phase1Api.updateCategory(form.id, payload);
      else await phase1Api.createCategory(payload);
      setMessage(form.id ? "Category updated." : "Category added to marketplace taxonomy.");
      setModalOpen(false);
      await load();
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.detail || data?.name?.[0] || "Category could not be saved.");
    }
  }

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 workspace-main category-management-main">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="records-hero category-hero-v2">
          <div>
            <span>MARKETPLACE TAXONOMY</span>
            <h2>Categories and subcategories</h2>
            <p>Control the product structure used by sellers, business accounts and the public marketplace.</p>
          </div>
          {canManage && <button type="button" onClick={() => openCategory()}>Add category</button>}
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        <section className="workspace-card category-manager-card">
          <div className="records-card__header">
            <div><span>LIVE CATEGORY DATA</span><h3>Marketplace category tree</h3></div>
            <button type="button" onClick={load}>Refresh</button>
          </div>

          {loading ? <div className="records-empty">Loading categories...</div> : (
            <div className="category-tree-grid">
              {parentCategories.map((parent) => {
                const children = rows.filter((item) => item.parent === parent.id);
                return (
                  <article key={parent.id} className="category-tree-card">
                    <button type="button" onClick={() => openCategory(parent)} className="category-tree-parent">
                      <div><strong>{parent.name}</strong><span>{parent.risk_level} risk • {parent.subcategory_count || children.length} subcategories</span></div>
                      <em>{parent.is_active ? "Active" : "Inactive"}</em>
                    </button>
                    <div className="category-tree-children">
                      {children.length ? children.map((child) => (
                        <button type="button" key={child.id} onClick={() => openCategory(child)}>
                          <span>{child.name}</span>
                          <small>{child.requires_manual_product_review ? "Review required" : "Normal"}</small>
                        </button>
                      )) : <p>No subcategories yet.</p>}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {modalOpen && (
        <Modal title={form.id ? "Edit category" : "Add category"} onClose={() => setModalOpen(false)}>
          <form className="modal-form-grid polished-form-grid" onSubmit={saveCategory}>
            <div><label>Category name</label><input value={form.name} onChange={(e) => update("name", e.target.value)} required /></div>
            <div><label>Parent category</label><select value={form.parent || ""} onChange={(e) => update("parent", e.target.value)}><option value="">Main category</option>{parentCategories.filter((item) => item.id !== form.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
            <div><label>Risk level</label><select value={form.risk_level} onChange={(e) => update("risk_level", e.target.value)}><option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option><option value="RESTRICTED">Restricted</option></select></div>
            <div><label>Display order</label><input type="number" value={form.display_order} onChange={(e) => update("display_order", e.target.value)} /></div>
            <div className="full"><label>Description</label><textarea value={form.description || ""} onChange={(e) => update("description", e.target.value)} rows="3" /></div>
            <label className="modal-check full"><input type="checkbox" checked={form.requires_manual_product_review} onChange={(e) => update("requires_manual_product_review", e.target.checked)} /> Products in this category require admin review</label>
            <label className="modal-check full"><input type="checkbox" checked={form.is_active} onChange={(e) => update("is_active", e.target.checked)} /> Category is active</label>
            <button type="submit">Save category</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
